import { corsReadUrl } from '../../services/mediaRead';

/*
 * Focus: a wide-open lens, as ONE picture. The cut-out model finds the subject; here, in the
 * browser, the background is blurred with the subject taken out of it first and the hole filled
 * from what is around it (a normalised blur: the colours blurred with the subject's weight at
 * nothing, divided by the blurred weight), so no halo of the subject smears into the blur. The
 * subject goes back on top, sharp, with the cut-out's own soft edge.
 *
 * It used to be two layers — the subject cut out above the picture, the picture blurred under it
 * (Tarek, 15 Oct: "something is weird about this"): the next press on the picture chose the
 * cut-out, so the next tool ran on that; the two drifted apart under Adjust; and the cut-out was
 * made from a small render. One picture, landed as a take, has none of that, and Adjust and the
 * layer's other effects go on applying to it exactly as they did to the picture before.
 */

/** How strong the lens is: the blur's radius as a share of the picture's long side. */
export const FOCUS_BLUR = 0.018;
/** The longest side the picture is composed at (a browser canvas blur of a 6000 px photo is slow). */
const MOST = 3072;

async function picture(url: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = corsReadUrl(url);
  await image.decode();
  return image;
}

function surface(width: number, height: number) {
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  return { canvas, g: canvas.getContext('2d', { willReadFrequently: true })! };
}

/** The picture with its subject sharp over a blurred background, as a PNG. `source` and `cutout` are readable urls. */
export async function focusComposite(source: string, cutout: string, strength = FOCUS_BLUR): Promise<Blob> {
  const [photo, subject] = await Promise.all([picture(source), picture(cutout)]);
  const k = Math.min(1, MOST / Math.max(photo.naturalWidth, photo.naturalHeight));
  const width = Math.max(1, Math.round(photo.naturalWidth * k)), height = Math.max(1, Math.round(photo.naturalHeight * k));
  const radius = Math.max(2, Math.round(Math.max(width, height) * strength));

  // The background alone: the picture with the subject's shape taken out.
  const holed = surface(width, height);
  holed.g.drawImage(photo, 0, 0, width, height);
  holed.g.globalCompositeOperation = 'destination-out';
  holed.g.drawImage(subject, 0, 0, width, height);

  // Blurred as it is held (premultiplied), then read back straight: each colour is the blurred
  // colour over the blurred weight — the hole is filled from around it. Made opaque again.
  const blurred = surface(width, height);
  blurred.g.filter = `blur(${radius}px)`;
  blurred.g.drawImage(holed.canvas, 0, 0);
  blurred.g.filter = 'none';
  const pixels = blurred.g.getImageData(0, 0, width, height);
  const original = holed.g.getImageData(0, 0, width, height).data;
  for (let i = 0; i < pixels.data.length; i += 4) {
    if (pixels.data[i + 3]! > 0) pixels.data[i + 3] = 255;
    // Nothing of the background within reach (a subject filling the frame): its own colour.
    else { pixels.data[i] = original[i]!; pixels.data[i + 1] = original[i + 1]!; pixels.data[i + 2] = original[i + 2]!; pixels.data[i + 3] = 255; }
  }
  // A picture that was transparent somewhere stays so there: the photo's own alpha over the result.
  const out = surface(width, height);
  out.g.putImageData(pixels, 0, 0);
  out.g.globalCompositeOperation = 'destination-in';
  out.g.drawImage(photo, 0, 0, width, height);
  out.g.globalCompositeOperation = 'source-over';

  // The subject, sharp, with the cut-out's edge.
  const sharp = surface(width, height);
  sharp.g.drawImage(photo, 0, 0, width, height);
  sharp.g.globalCompositeOperation = 'destination-in';
  sharp.g.drawImage(subject, 0, 0, width, height);
  out.g.drawImage(sharp.canvas, 0, 0);

  return new Promise((resolve, reject) => out.canvas.toBlob(blob => (blob ? resolve(blob) : reject(Error('The focused picture could not be made.'))), 'image/png'));
}
